/**
 * The menu, as data.
 *
 * Everything that decides whether an entry is DRAWN — the permission, the module
 * licence, the tenant-wide rule — is on the entry, and `libs/nav.ts` is the single
 * place that applies it. The shapes live in `types/Nav.ts`.
 *
 * The permission of every entry below is copied unchanged from the menu this replaces,
 * comments included, with ONE deliberate exception: the CRM entries ask `crm:read`
 * rather than `guest:read`, which is what the API has guarded `/crm/*` with all along
 * (migration 65 grants it to every role that held `guest:read`, so nobody loses a
 * screen). `crm-manager` already asked for it.
 */
import type { INavContext, INavGroup, INavTabGroup } from '@/types/Nav'

/**
 * The door into the marketing portal: a real address, followed by a real click.
 *
 * The server behind it mints the short-lived handoff and redirects, so this portal
 * still does not know where the marketing one lives — that was the reason the old
 * arrangement asked first, and it is preserved. What is gone is the await between
 * the click and the window, which is what the browser was blocking.
 *
 * It authenticates by the session cookie, which a top-level navigation carries.
 */
export const MARKETING_DOOR = '/api/pms/v1/marketing/open'

/**
 * Routes that belong to a menu entry without carrying its name.
 *
 * Written out rather than derived from a prefix, because a rule like "any route whose
 * name begins with this one" would quietly claim entries nobody meant it to — and the
 * cost of getting it wrong is a menu that highlights the wrong screen, which nobody
 * reports and everybody misreads.
 *
 * It is shorter than it was: `useNav`'s `isActive` now compares the ADDRESS of the
 * current screen with the address of each entry, so `/skupine/{id}` and
 * `/staff/people/{userId}` light their registers without being named here. What is
 * left is the pair that address cannot reach — a guest and a company are read under
 * `/crm/…` and their registers live at `/guests` and `/companies`.
 */
export const NESTED_ROUTES: Record<string, string> = {
    group:          'groups',
    'staff-person': 'staff-people',
    'crm-guest':    'guests',
    'crm-company':  'companies',
}

/**
 * The screens that are TABS of one screen rather than entries of their own
 * (phase 2 of the menu, v1.542.0).
 *
 * Materials arrived one work package at a time and each package brought its own menu
 * entry, so „Nabava in zaloga“ ended up with fourteen of them and Finance with eight —
 * a list nobody reads down, and a fold that had to be scrolled on a laptop. The screens
 * themselves were never the problem: six of them are the ONE job of buying something,
 * and a person doing that job moves between them all morning.
 *
 * So the menu names the job once and the screens become tabs of it. Nothing about a
 * screen changes — the route, the address, the permission and the module licence are
 * what they were, which is why every bookmark, every link in a mail and Milo's screen
 * register still land exactly where they did.
 *
 * The ORDER of the members in the manifest is the order of the tabs, and the FIRST one
 * this account may open is where the menu entry leads.
 *
 * „Zaposleni“ joined them in v1.544.0 for the same reason and with the same result: eleven
 * entries under three quiet headings, where the headings were already saying what the
 * job was. Leading the house's week is one job — make the rota, see whether it holds,
 * decide what moves it, read what it came to — and the personnel registers are another,
 * so the two headings become the two entries and the ten screens become their tabs. The
 * headings themselves go: a heading over a single entry that repeats the entry's own
 * word is a line of menu that says nothing twice.
 *
 * „Jaz“ is NOT one of them. It reads like a third group and is one screen — `/staff/me`
 * with four tabs of its own inside the page (docs/OSEBJE-UI.md §3), the other personal
 * addresses being redirects into it — and a tab group standing for a single screen is a
 * row of one tab, which `activeTabs` refuses to draw anyway. So it stays the plain entry
 * it always was.
 */
export const NAV_TAB_GROUPS: INavTabGroup[] = [
    { key: 'nabava',        labelKey: 'nav.materials_group_purchasing' },
    { key: 'prevzem',       labelKey: 'nav.materials_group_receiving' },
    { key: 'veriga',        labelKey: 'nav.materials_group_chain' },
    { key: 'knjigovodstvo', labelKey: 'nav.materials_group_bookkeeping' },
    // The two halves of „Zaposleni“. They borrow the keys the SECTIONS were labelled
    // with, because the label is the same word for the same reason — the heading was
    // already naming the job, it simply was not pressable.
    { key: 'vodenje',       labelKey: 'nav.staff_management' },
    { key: 'kadri',         labelKey: 'nav.staff_hr' },
]

/**
 * The seven folds of the working menu, in the order a desk works.
 *
 * „Danes“ first — the landing page, and a group of ONE (see below) — then reception,
 * because the chessboard and the arrivals are what is open all day, then what the house
 * sells, what it stages, what it keeps clean, what it buys, what it is paid, and the
 * people who do it. Everything that is SET UP once has left this list for the gear in
 * the top bar (`settingsNavGroups` below).
 *
 * # A group of one is not a fold
 *
 * „Danes“ is written as a group holding a single entry rather than as a loose item
 * beside the manifest, and that is not a shape chosen for tidiness. Everything that
 * reads this file reads GROUPS — the palette searches them, the favourites are drawn
 * from them, the active-entry rule resolves their addresses — so an entry that lived
 * outside them would be a screen the Ctrl+K palette cannot find and the highlighting
 * cannot light, which is exactly the state phase 1 was written to end.
 *
 * What is a fact about the SIDEBAR, and is settled by `flat: true` on the group, is that
 * it is drawn as the entry itself rather than as a heading that folds open to reveal a
 * single line.
 */
export const appNavGroups = (ctx: INavContext): INavGroup[] => {
    const { t } = ctx

    return [
        {
            // The one screen a person lands on after signing in (docs/DANES.md).
            //
            // No permission at all, and that is the point: it is not a screen somebody
            // is given, it is the page that gathers the ones they were. What it draws is
            // decided by the answer — a section this account may not read is absent from
            // it — so an entry gated on any one key would hide the page from precisely
            // the accounts whose morning it describes.
            key: 'danes', labelKey: 'nav.today', icon: 'i-lucide-sunrise', flat: true,
            sections: [
                {
                    key: 'danes',
                    items: [
                        { name: 'today', label: t('nav.today'), icon: 'i-lucide-sunrise', permission: '' },
                    ],
                },
            ],
        },
        {
            key: 'recepcija', labelKey: 'nav.group_reception', icon: 'i-lucide-key-round',
            sections: [
                {
                    key: 'recepcija',
                    items: [
                        { name: 'chessboard',   label: t('nav.chessboard'),   icon: 'i-lucide-layout-grid',   permission: 'reservation:read' },
                        { name: 'availability', label: t('nav.availability'), icon: 'i-lucide-door-open',     permission: 'reservation:read' },
                        { name: 'reservations', label: t('nav.reservations'), icon: 'i-lucide-calendar-days', permission: 'reservation:read' },
                        // Poročanje AJPES, straight after the bookings the filings are
                        // born on. It came out of Finance → Poročila on 14. 9. 2026, where
                        // it read as a month's statistic: a guest is filed within TWELVE
                        // HOURS of arrival (docs/ETURIZEM.md §2), which makes it the desk's
                        // work on the evening somebody walks in and not the accountant's on
                        // the first of the month.
                        //
                        // `compliance:read`, the key every read behind the screen is guarded
                        // by (HTTP_API/Compliance.go) — NOT `report:read`, which is what the
                        // tab inherited from the reports around it and which a receptionist
                        // does not hold. No module: reporting a guest is the law.
                        { name: 'registrations', label: t('nav.registrations'), icon: 'i-lucide-file-check', permission: 'compliance:read' },
                        { name: 'reminders',    label: t('nav.reminders'),    icon: 'i-lucide-bell',          permission: 'reservation:read' },
                        // The cars the desk arranges, keyed by reservation:read, which is
                        // what the route behind it asks for.
                        { name: 'transfers',    label: t('nav.transfers'),    icon: 'i-lucide-car-front',     permission: 'reservation:read' },
                        // `retail:sell` and NOT `retail:read` alone: this entry opens the
                        // COUNTER, and the counter is a keypad, a shift and a drawer — every
                        // one of which the screen itself refuses without `retail:sell`
                        // (`views/retail/Retail.vue`, `canSell`). What `retail:read` really
                        // opens — the tills and their shift history — is its own entry under
                        // the gear and keeps that key alone.
                        //
                        // All three, by the rule that an entry needs every right the screen's
                        // FIRST read needs: `/retail/tills` (`retail:read`), `/stock/items`,
                        // `/stock/places` and `/stock/balances` (`stock:read`) and
                        // `/retail/rooms` plus the shift (`retail:sell`). A seller who may
                        // not read the shelf gets a till with nothing on it.
                        //
                        // It stands with reception and no longer inside the materials fold:
                        // the till is a counter somebody works at all day, not a store
                        // cupboard screen.
                        {
                            name: 'retail', label: t('nav.retail'), icon: 'i-lucide-scan-barcode',
                            permission: 'retail:sell', allOf: ['retail:sell', 'retail:read', 'stock:read'], module: 'materials',
                        },
                    ],
                },
            ],
        },
        {
            key: 'prodaja', labelKey: 'nav.group_sales', icon: 'i-lucide-handshake',
            sections: [
                {
                    key: 'prodaja',
                    items: [
                        { name: 'offers',     label: t('nav.offers'),     icon: 'i-lucide-sparkle',        permission: 'reservation:read' },
                        { name: 'rate-plans', label: t('nav.rate_plans'), icon: 'i-lucide-tags',           permission: 'rate:read' },
                        { name: 'channels',   label: t('nav.channels'),   icon: 'i-lucide-satellite-dish', permission: 'channel:read', module: 'distribution' },
                        // The relationship side of the house: the overview reads the rest, so
                        // it leads; guests and companies are the two subjects; segments, the
                        // funnel and the to-do list act on them.
                        //
                        // `crm:read` on all of them, which is the key the API has guarded
                        // `/crm/*` with since the module landed. The menu asked `guest:read`,
                        // so a role holding one and not the other was shown six entries the
                        // service then refused. Migration 65 grants `crm:read` to every role
                        // that had `guest:read`, so the change takes nothing away.
                        { name: 'crm-dashboard',     label: t('nav.crm_overview'),  icon: 'i-lucide-layout-dashboard', permission: 'crm:read' },
                        { name: 'crm-manager',       label: t('nav.crm_manager'),   icon: 'i-lucide-chart-line',       permission: 'crm:read' },
                        { name: 'guests',            label: t('nav.guests'),        icon: 'i-lucide-users',            permission: 'crm:read' },
                        { name: 'companies',         label: t('nav.companies'),     icon: 'i-lucide-briefcase',        permission: 'crm:read' },
                        { name: 'crm-segments',      label: t('nav.segments'),      icon: 'i-lucide-filter',           permission: 'crm:read' },
                        { name: 'crm-opportunities', label: t('nav.opportunities'), icon: 'i-lucide-trending-up',      permission: 'crm:read' },
                        { name: 'crm-tasks',         label: t('nav.tasks'),         icon: 'i-lucide-check-check',      permission: 'crm:read' },
                        // MARKETING. Not a route of this portal but a doorway out of it: the
                        // module is its own application on its own subdomain
                        // (docs/MARKETING.md §2). It shows where the house HAS the module and
                        // the account may read it — the licence as well as the permission,
                        // although either would do on its own today, because the rule
                        // everywhere in this menu is that the navigation is built from the
                        // licence.
                        {
                            name: 'marketing', label: t('nav.marketing'), icon: 'i-lucide-megaphone',
                            permission: 'marketing:read', module: 'marketing', external: true, href: MARKETING_DOOR,
                        },
                    ],
                },
            ],
        },
        {
            key: 'dogodki', labelKey: 'nav.group_events', icon: 'i-lucide-party-popper',
            sections: [
                {
                    key: 'dogodki',
                    items: [
                        { name: 'groups',     label: t('nav.groups'),     icon: 'i-lucide-users-round', permission: 'reservation:read', module: 'groups_events' },
                        // The events sold into those halls (docs/DOGODKI.md §3.1). `event:read`
                        // and not `facility:read`: the wellness desk holds the second and two
                        // therapists have it, and a banqueting contract for 14 000 € is not the
                        // same act. Same licence key as the halls and the groups — MICE is one
                        // story and one module.
                        { name: 'events',     label: t('nav.events'),     icon: 'i-lucide-party-popper', permission: 'event:read',    module: 'groups_events' },
                        { name: 'facilities', label: t('nav.facilities'), icon: 'i-lucide-waves',       permission: 'facility:read', module: 'groups_events' },
                        { name: 'dining',     label: t('nav.dining'),     icon: 'i-lucide-utensils',    permission: 'dining:read' },
                    ],
                },
            ],
        },
        {
            key: 'hisa', labelKey: 'nav.group_house', icon: 'i-lucide-house',
            sections: [
                {
                    key: 'hisa',
                    items: [
                        // The morning of the floor, read-only. `housekeeping:read` is the fence
                        // and it is the right one: a receptionist holds no housekeeping key at
                        // all (docs/PRAVICE.md §5), so the entry appears for the head of
                        // reception and for management — which is who asked for the screen.
                        { name: 'housekeeping-overview', label: t('nav.housekeeping_overview'), icon: 'i-lucide-brush-cleaning', permission: 'housekeeping:read', module: 'operations' },
                        { name: 'lost-items',            label: t('nav.lost_items'),            icon: 'i-lucide-package-search', permission: 'housekeeping:read', module: 'operations' },
                        { name: 'maintenance',           label: t('nav.maintenance'),           icon: 'i-lucide-wrench',         permission: 'maintenance:read',  module: 'operations' },
                    ],
                },
            ],
        },
        {
            key: 'nabava', labelKey: 'nav.group_procurement', icon: 'i-lucide-warehouse',
            /**
             * Imprion Materials, the module (docs/MATERIALNO.md §10).
             *
             * Every entry carries `module: 'materials'`, so a house that has not bought it
             * draws no fold at all. The licence is asked and not only the permission:
             * `stock:read` and `retail:read` are deliberately NOT masked when the module is
             * off (pkg/enums/Module, permissionOwner), because the housekeeping portal reads
             * a trolley with the first of them. So here the licence is the whole of the
             * fence, and a permission alone would draw a menu into screens the server
             * refuses.
             *
             * SIX entries and not fourteen (v1.542.0). The shelf keeps its three —
             * „Pregled“, „Zaloga“ and „Inventure“ are each read on their own — and the
             * other eleven are three jobs rather than eleven screens: buying, receiving,
             * and the chain around them. Each job is one entry here and a row of tabs
             * above the screen it opens, in the order the work is done. The fourth job,
             * the book-keeping, is under Finance where its reader already is.
             */
            sections: [
                {
                    // No heading: the overview is the fold's front door and stands above the
                    // three, not inside one of them.
                    key: 'pregled',
                    items: [
                        /**
                         * „Pregled“ — the one screen that answers „kaj me čaka“ across the
                         * whole module (docs/MATERIALNO.md §10.11).
                         *
                         * `anyOf` and not one key, because the screen is panels and each panel
                         * has its own: a storekeeper, a buyer, an approver, a book-keeper and
                         * a director all belong here and no two of them hold the same thing.
                         * `permission` is the first of the five and is never consulted while
                         * `anyOf` is set (see `allow`) — it is there because every entry
                         * carries one.
                         *
                         * `report:read` is deliberately NOT in the list although the
                         * director's panel opens on it. It is not a `materials:*` key, so the
                         * licence mask does not take it away, and an entry standing on it
                         * alone would survive into the menu of a house that never bought the
                         * module.
                         */
                        {
                            name: 'materials-overview', label: t('nav.materials_overview'), icon: 'i-lucide-list-todo',
                            permission: 'stock:read', module: 'materials',
                            anyOf: ['stock:read', 'materials:procure', 'materials:approve', 'materials:accounting'],
                        },
                        { name: 'stock',            label: t('nav.stock'),            icon: 'i-lucide-boxes',           permission: 'stock:read',      module: 'materials' },
                        // The supervisor's own screen: `stock:inventory` is the right that
                        // starts a stocktake and signs off its posting
                        // (docs/MATERIALNO-MOBILNO.md, WP-M7).
                        { name: 'materials-counts', label: t('nav.materials_counts'), icon: 'i-lucide-clipboard-check', permission: 'stock:inventory', module: 'materials' },
                    ],
                },
                {
                    /**
                     * Nabava — everything between deciding to buy something and being
                     * allowed to. ONE entry in the menu („Nabava“) and six tabs above the
                     * screen, in the order the work is done: the order, the enquiry behind
                     * its price, the requisition that asked for it, the approval that let
                     * it out of the house, and the two codebooks the whole of it stands on.
                     *
                     * The section draws no heading of its own any more — the entry the
                     * tabs hang under says the same word, and a heading over a single
                     * entry is a line of screen saying nothing.
                     */
                    key: 'nabava',
                    items: [
                        // The buying half (docs/MATERIALNO-NAROCILA.md, WP-M3b): approving an
                        // order is the house committing to spend and sending one is a letter
                        // that leaves the building with the hotel's name on it.
                        //
                        // FIRST, and therefore the screen „Nabava“ opens on: it is where a
                        // buyer's morning starts, and its right (`materials:procure`) is the
                        // one the rest of this group is built around.
                        { name: 'materials-orders', label: t('nav.materials_orders'), icon: 'i-lucide-clipboard-list', permission: 'materials:procure', module: 'materials', tabs: 'nabava' },
                        { name: 'materials-rfqs',   label: t('nav.materials_rfqs'),   icon: 'i-lucide-mails',          permission: 'materials:procure', module: 'materials', tabs: 'nabava' },
                        // The asking half (docs/MATERIALNO-ZAHTEVKI.md, WP-M4). The register is
                        // opened by EITHER key: a chef raises requisitions and never approves
                        // one, a director approves them and never writes one, and both need the
                        // same list.
                        {
                            name: 'materials-requisitions', label: t('nav.materials_requisitions'), icon: 'i-lucide-file-input',
                            permission: 'materials:requisition', anyOf: ['materials:requisition', 'materials:approve'], module: 'materials',
                            tabs: 'nabava',
                        },
                        // The number of requisitions waiting for this account is the one badge
                        // this fold carries, and it is why the group entry ADDS its members'
                        // badges up: an approver who sees „Nabava 3“ still learns the thing the
                        // badge was put there to tell them, without the entry being drawn.
                        {
                            name: 'materials-approvals', label: t('nav.materials_approvals'), icon: 'i-lucide-stamp',
                            permission: 'materials:approve', module: 'materials', badge: ctx.pendingApprovals,
                            tabs: 'nabava',
                        },
                        // The two codebooks the tabs above stand on, at the end of the row
                        // because they are consulted rather than worked in.
                        { name: 'suppliers', label: t('nav.suppliers'), icon: 'i-lucide-building-2', permission: 'stock:read', module: 'materials', tabs: 'nabava' },
                        // The buyer's own door (docs/MATERIALNO-KATALOG.md, WP-M3a) — the
                        // supplier's catalogue and the contracts behind its prices.
                        // `materials:procure` and NOT `stock:read`: what the house AGREES TO
                        // PAY is not the same authority as reading a shelf.
                        { name: 'materials-catalogue', label: t('nav.materials_catalogue'), icon: 'i-lucide-book-marked', permission: 'materials:procure', module: 'materials', tabs: 'nabava' },
                        // „Pravila odobritev“ was here and is under the gear now: writing the
                        // ladder is deciding how the house's money is committed, which is set
                        // up once and not opened on a morning shift.
                    ],
                },
                {
                    /**
                     * Prevzem — the goods arriving, and what goes back. One entry, three
                     * tabs.
                     *
                     * The right is the storekeeper's (`stock:*`), the module is Materials — a
                     * house without it has no quarantine and nothing to return. „Mobilno“
                     * stands on `stock:read`, the lowest of the three flows behind it: the hub
                     * draws only the cards the account may walk into.
                     */
                    key: 'prevzem',
                    items: [
                        { name: 'materials-receiving-issues', label: t('nav.materials_receiving'), icon: 'i-lucide-package-search', permission: 'stock:read', module: 'materials', tabs: 'prevzem' },
                        { name: 'materials-returns',          label: t('nav.materials_returns'),  icon: 'i-lucide-undo-2',         permission: 'stock:read', module: 'materials', tabs: 'prevzem' },
                        { name: 'materials-mobile',           label: t('nav.materials_mobile'),   icon: 'i-lucide-smartphone',     permission: 'stock:read', module: 'materials', tabs: 'prevzem' },
                    ],
                },
                {
                    /**
                     * Veriga — buying as a group, and stock moving between houses (WP-M5,
                     * docs/VERIGE-ZASLONI.md). One entry, two tabs.
                     *
                     * Both carry the `chains` licence and not `materials`: the licence being
                     * asked about is the chain one, and a house that bought Materials alone has
                     * no chain to draw — so this entry disappears on its own for every
                     * single hotel.
                     */
                    key: 'veriga',
                    items: [
                        // The RIGHTS are the ordinary two — what the chain agrees to pay is the
                        // buyer's authority (`materials:procure`), and stock that leaves one
                        // shelf for another house is the storekeeper's (`stock:post`).
                        // `chains:read` beside the buyer's key: the screen's first call is
                        // `GET /properties/{id}/chain/`, and without that right the page drew
                        // „ta hiša ni v verigi“ — a house being told it has no chain because it
                        // may not read one.
                        {
                            name: 'materials-chain', label: t('nav.materials_chain'), icon: 'i-lucide-network',
                            permission: 'materials:procure', allOf: ['chains:read', 'materials:procure'], module: 'chains',
                            tabs: 'veriga',
                        },
                        // The transfers stand on BOTH licences and say so: a house with
                        // Materials and no chain has nowhere to send anything, and one with a
                        // chain and no Materials has no shelf for the goods to leave.
                        // `stock:post` is deliberately outside the `materials:*` family the
                        // licence masks, so without both names here the entry would survive a
                        // missing Materials module.
                        { name: 'materials-transfers', label: t('nav.materials_transfers'), icon: 'i-lucide-truck', permission: 'stock:post', modules: ['materials', 'chains'], tabs: 'veriga' },
                    ],
                },
            ],
        },
        {
            key: 'finance', labelKey: 'nav.group_finance', icon: 'i-lucide-wallet',
            sections: [
                {
                    key: 'finance',
                    items: [
                        // Finance carries the invoices as its Računi tab, so the entry shows for
                        // either key.
                        { name: 'finance', label: t('nav.finance'), icon: 'i-lucide-wallet', permission: 'finance:read', anyOf: ['finance:read', 'invoice:read'] },
                    ],
                },
                {
                    /**
                     * Knjigovodstvo materiala — what the house is charged, and where it
                     * books it. One entry and six tabs (v1.542.0): the book-keeper who
                     * opens the invoices in the morning ends the month on the close, and
                     * every screen between the two is the same person's work.
                     */
                    key: 'knjigovodstvo',
                    items: [
                        // The book-keeper's own door, on `materials:invoice` rather than
                        // `stock:read` (docs/MATERIALNO.md §10.8). Unlike the shelf screens that
                        // right IS masked when the licence lapses (pkg/enums/Module,
                        // permissionOwner), so the entry disappears with the module rather than
                        // only being refused behind it. `stock:read` beside it: the register
                        // reads the supplier codebook FIRST and every row's name comes out of
                        // it, and an account that may read no supplier at all has no business
                        // in a list whose every row would be nameless.
                        {
                            name: 'materials-invoices', label: t('nav.materials_invoices'), icon: 'i-lucide-file-text',
                            permission: 'materials:invoice', allOf: ['materials:invoice', 'stock:read'], module: 'materials',
                            tabs: 'knjigovodstvo',
                        },
                        // The accounting half (docs/MATERIALNO-KNJIGOVODSTVO.md). Its own right
                        // — `materials:accounting` — because a chart of accounts and a button
                        // that writes into the company's books are not the same authority as
                        // reading a shelf.
                        { name: 'materials-accounting', label: t('nav.materials_accounting'), icon: 'i-lucide-book-open', permission: 'materials:accounting', module: 'materials', tabs: 'knjigovodstvo' },
                        // The planning half (WP-M6, docs/MATERIALNO-NAPOVED-ZASLONI.md). The
                        // forecast is the BUYER's screen — it suggests what to order and writes
                        // a requisition — so it stands on `materials:procure`; the budgets are
                        // the chart of what the house meant to spend and stand on
                        // `materials:accounting`, the same key as the books beside them.
                        { name: 'materials-budgets',  label: t('nav.materials_budgets'),  icon: 'i-lucide-wallet',      permission: 'materials:accounting', module: 'materials', tabs: 'knjigovodstvo' },
                        { name: 'materials-forecast', label: t('nav.materials_forecast'), icon: 'i-lucide-trending-up', permission: 'materials:procure',    module: 'materials', tabs: 'knjigovodstvo' },
                        // The management half (docs/MATERIALNO-KAZALNIKI.md,
                        // docs/MATERIALNO-ZAKLJUCEK.md). The indicators stand on `anyOf`,
                        // because the route is a guardAny over the accounting key and
                        // `report:read`: a general manager answers for what a room-night costs
                        // and holds the second of those. The close is the accounting key alone —
                        // pressing it stops everybody else posting into the month.
                        { name: 'materials-kpi',          label: t('nav.materials_kpi'),          icon: 'i-lucide-gauge',          permission: 'materials:accounting', anyOf: ['materials:accounting', 'report:read'], module: 'materials', tabs: 'knjigovodstvo' },
                        { name: 'materials-period-close', label: t('nav.materials_period_close'), icon: 'i-lucide-calendar-check', permission: 'materials:accounting', module: 'materials', tabs: 'knjigovodstvo' },
                    ],
                },
                {
                    // The reports sit under the money, where their reader goes next.
                    key: 'porocila',
                    items: [
                        { name: 'reports', label: t('nav.reports'), icon: 'i-lucide-chart-column', permission: 'report:read', module: 'reports' },
                    ],
                },
            ],
        },
        {
            key: 'zaposleni', labelKey: 'nav.staff_group', icon: 'i-lucide-users-round',
            /**
             * The people who work here, in three parts (docs/OSEBJE-UI.md §2): what is
             * MINE, what I LEAD and the personnel registers.
             *
             * Three ENTRIES since v1.544.0, where there were eleven under three quiet
             * headings. Two of the three parts are tab groups — „Vodenje“ and „Kadri“ —
             * so the fold names the job and the screens behind it are a row of tabs above
             * the page; „Jaz“ is one screen and stays one entry. The headings are gone
             * with them: a heading is what a run of entries needed, and there are no longer
             * runs to head.
             *
             * Each entry names the keys its own route is guarded by, so nothing here offers
             * a screen that would answer 403 — and each names its module. The masking alone
             * would very nearly do it, but the portal reads the licence itself
             * (docs/MODULI.md §3.3), so the fold is drawn from the licence rather than from
             * a side effect of it.
             */
            sections: [
                {
                    /** Osebno — one page, about the person reading it. No permission; §13 says why. */
                    key: 'osebno',
                    items: [
                        {
                            // „Jaz“ (docs/OSEBJE-UI.md §3). The ROUTE needs no permission and the
                            // API checks none — it narrows to the signed-in account instead — so
                            // the module is the real fence. A housekeeper, a waiter, a night
                            // porter — everybody the record is actually about — clocks in here,
                            // reads their own hours and opens their own contract, and
                            // docs/OSEBJE.md §13 is explicit that all of that is self-service.
                            name: 'staff-me', label: t('nav.me'), icon: 'i-lucide-user-round',
                            permission: '', module: 'staff',
                        },
                    ],
                },
                {
                    /**
                     * Vodenje — the week, in the order it is worked: make it, check it holds,
                     * decide what moves it, then read what it came to. One entry and seven
                     * tabs since v1.544.0, and the order below is the order of the row.
                     *
                     * „Danes“ stands BEFORE the week and is the one entry here that is not part
                     * of that order. It is what the person on duty opens at seven in the
                     * morning — the six things about this day that somebody has to do something
                     * about — and a screen answering „what is wrong right now“ filed after five
                     * screens about the month is a screen nobody reaches in time.
                     */
                    key: 'vodenje',
                    items: [
                        {
                            // The same four keys the read carries. Any one of them is somebody
                            // who leads part of this house, and the answer is narrowed to their
                            // own departments by the server (docs/OSEBJE.md §13).
                            name: 'staff-today', label: t('nav.staff_today'), icon: 'i-lucide-sunrise',
                            permission: 'staff:schedule', anyOf: ['staff:schedule', 'staff:oversee', 'staff:manage', 'staff:approve'], module: 'staff',
                            tabs: 'vodenje',
                        },
                        {
                            name: 'staff-rota', label: t('nav.rota'), icon: 'i-lucide-calendar-days',
                            permission: 'staff:read', anyOf: ['staff:schedule', 'staff:read'], module: 'staff',
                            tabs: 'vodenje',
                        },
                        {
                            name: 'staff-coverage', label: t('nav.coverage'), icon: 'i-lucide-gauge',
                            permission: 'staff:read', anyOf: ['staff:read', 'staff:oversee'], module: 'staff',
                            tabs: 'vodenje',
                        },
                        {
                            // Deciding leave and entering days into the record. `staff:approve`
                            // and not `staff:time`: looking at the record and moving it are
                            // different rights (docs/OSEBJE.md §13).
                            name: 'staff-absences', label: t('nav.absences'), icon: 'i-lucide-calendar-off',
                            permission: 'staff:approve', module: 'staff', tabs: 'vodenje',
                        },
                        {
                            // Deciding corrections, for the same reason and by the same key.
                            name: 'staff-time-corrections', label: t('nav.time_corrections'), icon: 'i-lucide-pencil-line',
                            permission: 'staff:approve', module: 'staff', tabs: 'vodenje',
                        },
                        {
                            // The house's record. `staff:time`, which is the key the API guards
                            // the read with — an entry leading to a screen that answers 403 is a
                            // lie.
                            name: 'staff-time', label: t('nav.time_record'), icon: 'i-lucide-calendar-clock',
                            permission: 'staff:time', module: 'staff', tabs: 'vodenje',
                        },
                        {
                            // The year's leave, per person. Same key as the record beside it and
                            // for the same reason: a balance is a reading of the record. Issuing
                            // a decision needs `staff:manage`, which the screen gates rather than
                            // the menu.
                            name: 'staff-vacations', label: t('nav.vacations'), icon: 'i-lucide-tree-palm',
                            permission: 'staff:time', module: 'staff', tabs: 'vodenje',
                        },
                    ],
                },
                {
                    /**
                     * Kadri — the cards, and the registers the cards name. One entry and
                     * three tabs: the card is opened from the register beside it and the
                     * organigram is the same two registers drawn as a shape, so somebody
                     * filing a work period moves between all three in one sitting.
                     */
                    key: 'kadri',
                    items: [
                        {
                            name: 'staff-people', label: t('nav.employments'), icon: 'i-lucide-id-card',
                            permission: 'staff:manage', module: 'staff', tabs: 'kadri',
                        },
                        {
                            // The departments and the posts. `staff:manage` and not a register
                            // key of its own: somebody who may not file a work period has no
                            // business inventing the department it would name (docs/OSEBJE.md §5).
                            name: 'staff-catalogue', label: t('nav.staff_catalogue'), icon: 'i-lucide-list-tree',
                            permission: 'staff:manage', module: 'staff', tabs: 'kadri',
                        },
                        {
                            // The same two registers seen as the house's own structure (§17). It
                            // reads with `staff:read` and the drag on it asks for `staff:manage`,
                            // which the page itself gates.
                            name: 'staff-org-chart', label: t('nav.staff_org_chart'), icon: 'i-lucide-network',
                            permission: 'staff:read', module: 'staff', tabs: 'kadri',
                        },
                    ],
                },
            ],
        },
    ]
}

/**
 * What lives behind the gear: everything a house decides ONCE.
 *
 * They were a fold at the bottom of the working menu, which is the wrong shape for
 * them twice over — a desk reads the entries above them all day and opens these a few
 * times a year, and twenty-two entries in one alphabetical-by-accident list is a list
 * nobody finds anything in. Seven headings, each one a question somebody arrives with.
 *
 * Every entry keeps the permission, module and tenant-wide rule it had in that fold.
 * The same groups are what the settings hub (`views/settings/SettingsHub.vue`) draws
 * as cards, so a person with few rights sees few cards and no empty ones.
 */
export const settingsNavGroups = (ctx: INavContext): INavGroup[] => {
    const { t } = ctx

    return [
        {
            key: 'objekt', labelKey: 'nav.settings_property', icon: 'i-lucide-building-2',
            sections: [
                {
                    key: 'objekt',
                    items: [
                        // The house itself: set up once — a hotel adds a room type in the
                        // spring and does not touch it again.
                        { name: 'properties', label: t('nav.properties'), icon: 'i-lucide-building-2', permission: 'property:read' },
                        // The AJPES account the business register is read on
                        // (docs/PODJETJA.md §10). property:write like the other credential
                        // screens, and for the sharper reason: what is on this page is bought by
                        // the unit and spent by the question.
                        { name: 'settings-business-register', label: t('business_register.nav'), icon: 'i-lucide-library', permission: 'property:write' },
                    ],
                },
            ],
        },
        {
            key: 'ljudje', labelKey: 'nav.settings_people', icon: 'i-lucide-shield-check',
            sections: [
                {
                    key: 'ljudje',
                    items: [
                        { name: 'settings-users', label: t('nav.users'), icon: 'i-lucide-users', permission: 'user:read', tenantWide: true },
                        // The roles themselves. `user:manage` and not `user:read`: the page
                        // exists to CHANGE what a role allows, and the read-only view of the
                        // same bundles is already on the accounts screen.
                        { name: 'settings-roles', label: t('nav.roles'), icon: 'i-lucide-shield-check', permission: 'user:manage', tenantWide: true },
                        // The house's revision trail (docs/REVIZIJSKA_SLED.md). `audit:read` and
                        // deliberately not property:write, which every credential screen uses:
                        // the trail names who did what minute by minute, INCLUDING the person
                        // reading it, and "may configure the hotel" is not the same authority as
                        // "may read everybody's day".
                        { name: 'settings-audit', label: t('audit.nav'), icon: 'i-lucide-scroll-text', permission: 'audit:read' },
                        // Everything a house decides once about its people, on one tabbed page:
                        // the working-time record and the corridor tablets, the holiday
                        // calendar, the leave rules and the document templates
                        // (docs/OSEBJE-UI.md §2). `staff:manage`: somebody who may not file a
                        // work period has no business deciding whether the house stamps breaks.
                        { name: 'settings-staff', label: t('nav.staff_settings'), icon: 'i-lucide-users-round', permission: 'staff:manage', module: 'staff' },
                    ],
                },
            ],
        },
        {
            key: 'finance', labelKey: 'nav.settings_finance', icon: 'i-lucide-landmark',
            sections: [
                {
                    key: 'finance',
                    items: [
                        { name: 'settings-tills', label: t('retail.tills'), icon: 'i-lucide-store', permission: 'retail:read' },
                        // The municipality's rate card and the grounds for not paying it
                        // (docs/ETURIZEM.md §6). `compliance:read`, matching the route's own
                        // guard: the rates and the relief list are what the house files with the
                        // state.
                        { name: 'settings-tourist-tax', label: t('tourist_tax.nav'), icon: 'i-lucide-landmark', permission: 'compliance:read' },
                    ],
                },
            ],
        },
        {
            key: 'gost', labelKey: 'nav.settings_guest', icon: 'i-lucide-message-circle-heart',
            sections: [
                {
                    key: 'gost',
                    items: [
                        // Whether the house's assistant answers a guest, and what he may
                        // prepare for them (plan §11a). property:write, matching the route it
                        // writes through, and `guest` because it is a switch on the guest portal
                        // — a house without that module has no portal for Milo to stand in.
                        { name: 'settings-milo', label: t('milo_settings.nav'), icon: 'i-lucide-message-circle-heart', permission: 'property:write', module: 'guest' },
                        // What the guest portal LOOKS like (docs/PLAN_GOST.md §A2).
                        // property:READ and not write: nothing on it is a secret — it is the
                        // hotel's own name and two colours, printed on a page any guest can open
                        // — so hiding it from somebody who may read the property would hide the
                        // ANSWER to "what does our portal say". Saving still asks property:write.
                        { name: 'settings-guest-portal', label: t('settings_guest_portal.nav'), icon: 'i-lucide-palette', permission: 'property:read', module: 'guest' },
                        // property:write because a screen on a wall is the house's own
                        // equipment, and which machines may talk to its data is its decision.
                        // One entry for the room televisions AND the foyer panels since 1.494.1
                        // (docs/DISPLAY.md §10).
                        { name: 'settings-screens', label: t('screens.nav'), icon: 'i-lucide-tv-minimal', permission: 'property:write' },
                        // property:write like the mail settings: it holds the tokens that post
                        // as the hotel, so reading the screen is already a privilege.
                        { name: 'settings-publishing', label: t('publishing.nav'), icon: 'i-lucide-megaphone', permission: 'property:write', module: 'marketing' },
                    ],
                },
            ],
        },
        {
            key: 'operativa', labelKey: 'nav.settings_operations', icon: 'i-lucide-sliders-horizontal',
            sections: [
                {
                    key: 'operativa',
                    items: [
                        { name: 'settings-dining',       label: t('nav.dining'),       icon: 'i-lucide-utensils',       permission: 'dining:manage' },
                        // property:write, because what is on this screen is the house's own
                        // equipment: which machines may talk to its data, which is not a
                        // housekeeping decision.
                        { name: 'settings-housekeeping', label: t('nav.housekeeping'), icon: 'i-lucide-brush-cleaning', permission: 'property:write', module: 'operations' },
                        // The rules are their OWN key. Approving a requisition is answering the
                        // question the house has already decided; writing the rules is deciding
                        // it — from what amount, by whom, and what happens when the deadline
                        // passes. An approver who may also rewrite the ladder can approve
                        // anything by first lowering the rung, which is the one thing the ladder
                        // exists to stop. It is set up once, so it is here rather than beside
                        // the requisitions.
                        {
                            name: 'materials-approval-rules', label: t('nav.materials_approval_rules'), icon: 'i-lucide-list-checks',
                            permission: 'materials:rules', module: 'materials',
                        },
                        // Which system reads this house's rooms, and where its equipment stands.
                        { name: 'settings-grms', label: t('grms.nav'), icon: 'i-lucide-cpu', permission: 'property:write' },
                        // The demonstration console, and the one entry in this menu that a
                        // permission alone does not decide. It exists ONLY where the house runs
                        // simulated equipment — a hotel with controllers in its rooms has no
                        // such screen at all, because a console offering to open a window in an
                        // occupied room is a console somebody eventually presses
                        // (docs/SOBNA-TELEMETRIJA.md §4.7.8b).
                        {
                            name: 'settings-demo', label: t('demo.nav'), icon: 'i-lucide-flask-conical',
                            permission: 'telemetry:automation', when: ctx.demoConsole,
                        },
                    ],
                },
            ],
        },
        {
            key: 'povezave', labelKey: 'nav.settings_links', icon: 'i-lucide-plug',
            sections: [
                {
                    key: 'povezave',
                    items: [
                        // Which houses buy as one (WP-M5). `chains:manage` and not
                        // `chains:read`: the page exists to CHANGE the chain, and reading who is
                        // in one is on „Nabava in zaloga › Veriga“, beside the prices it
                        // decides. `tenantWide`, because `/chains` is outside `/properties` — an
                        // account assigned to a single house resolves no permission there.
                        { name: 'settings-chains', label: t('chain.nav'), icon: 'i-lucide-network', permission: 'chains:manage', module: 'chains', tenantWide: true },
                        // Whether the assistants may answer how busy the town is, and the TomTom
                        // key they read the road through (docs/AI.md §29). property:write like
                        // every other credential screen, and deliberately NO module: the reading
                        // is shared by the desk's own Milo and the guest portal's.
                        { name: 'settings-city-pulse', label: t('city_pulse.nav'), icon: 'i-lucide-activity', permission: 'property:write' },
                        // The ordering PIN the guest's phone and the room TV ask for before
                        // an order or a booking. property:write, no module: two doors, and
                        // the television is not the guest portal.
                        { name: 'settings-order-pin', label: t('order_pin.nav'), icon: 'i-lucide-key-round', permission: 'property:write' },
                        // How the house reaches its own people (docs/OBVESCANJE.md).
                        // property:write because it speaks as the hotel. NO module: the
                        // transport is house-wide, and the rota is only the first thing to use
                        // it.
                        { name: 'settings-notifications', label: t('notifications.nav'), icon: 'i-lucide-bell-ring', permission: 'property:write' },
                        // Two keys, matching the route's own guard (HTTP_API/AIUsage.go): the
                        // accountant who will check the surcharge, and the person who switched
                        // the paid feature on. Either is owed the sight of what it costs.
                        {
                            name: 'settings-ai-usage', label: t('ai_usage.nav'), icon: 'i-lucide-gauge',
                            permission: 'finance:read', anyOf: ['finance:read', 'property:write'],
                        },
                    ],
                },
            ],
        },
        {
            key: 'brskalnik', labelKey: 'nav.settings_browser', icon: 'i-lucide-palette',
            sections: [
                {
                    key: 'brskalnik',
                    items: [
                        // Everybody may change their own language, their own theme and, since
                        // this menu, which menu they get.
                        { name: 'settings-appearance', label: t('nav.appearance'), icon: 'i-lucide-palette', permission: '' },
                    ],
                },
            ],
        },
    ]
}

/**
 * Every route name that lives behind the gear, whatever this account may open.
 *
 * Built from the manifest itself and NOT from the filtered menu, because it answers a
 * question about the application rather than about the person: "is this screen part of
 * the settings?" has the same answer before the permissions have arrived as after, and
 * the sidebar has to decide which mode it is in on the first paint.
 *
 * Two of the entries do not live under /settings at all — the register of houses is
 * /properties and the approval ladder is /materials/approval-rules — which is exactly
 * why a path prefix cannot be the test.
 */
export const SETTINGS_ROUTE_NAMES: ReadonlySet<string> = new Set(
    settingsNavGroups({ t: (key: string) => key, pendingApprovals: 0, demoConsole: true })
        .flatMap(group => group.sections.flatMap(section => section.items.map(item => item.name))),
)
